import React from "react";
import {
  Box,
  Typography,
  Skeleton,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Stack,
  Chip,
  Button,
  Avatar,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { HistoryOutlined as HistoryIcon } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import MailOutlineIcon from "@mui/icons-material/Email";
import CurrencyPoundIcon from "@mui/icons-material/CurrencyPound";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EuroIcon from "@mui/icons-material/Euro";
import PhoneIcon from "@mui/icons-material/Phone";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import theme from "@/utils/theme";
import { formatInBackofficeLondon } from "@/utils/dateUtils";
import { ParticipantResponse } from "@/domain/participant.interface";
import ProfileMenu from "@/ui/menus/clients/ProfileMenu";
import TimelineLinkButton from "@/ui/content/troubleshooting/TimelineLinkButton";
import { useCurrencySetting } from "@/hooks/useSetting";
import { copyToClipboard } from "@/utils/copyToClipboard";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useUserStore } from "@/hooks/useUserStore";
import { useTranslation } from "react-i18next";

interface ProfileCardsProps {
  participantResponse: ParticipantResponse;
  isLoadingParticipant: boolean;
  isErrorParticipant?: boolean;
  handleEditParticipant: () => void;
  handleAuditLogs: () => void;
  handleDelete: () => void;
  handleTransactionHistory: () => void;
}

const ProfileCards: React.FC<ProfileCardsProps> = ({
  participantResponse,
  isLoadingParticipant,
  isErrorParticipant,
  handleEditParticipant,
  handleAuditLogs,
  handleDelete,
  handleTransactionHistory,
}) => {
  const { participant } = participantResponse;
  const currency = useCurrencySetting();
  const { userHasAccessToCode } = useUserStore();
  const { t } = useTranslation("participant");

  const { showSnackBar } = useFeedBack();

  // Check if user has permission to view incentive amounts
  const canViewIncentives = userHasAccessToCode("PARTICIPANT_VIEW_INCENTIVES");
  const canViewBalance = userHasAccessToCode("PARTICIPANT_VIEW_REWARD_BALANCE");

  // Log error condition for monitoring and troubleshooting
  React.useEffect(() => {
    if (isErrorParticipant) {
      console.error(
        "[ProfileCards] Error retrieving participant data:",
        "Backend or network error while retrieving participant's reward balance and incentive information",
      );
    }
  }, [isErrorParticipant]);

  // Format incentive amount based on permission and error state
  const formatIncentiveAmount = (amount?: number): string => {
    if (isErrorParticipant) {
      return "-.--";
    }
    if (!canViewIncentives) {
      return "*.**";
    }
    return amount?.toFixed(2) || "0.00";
  };

  const formatBalanceAmount = (amount?: number): string => {
    if (isErrorParticipant) {
      return "-.--";
    }
    if (!canViewBalance) {
      return "*.**";
    }
    return amount?.toFixed(2) || "0.00";
  };

  // Get currency icon based on currency setting
  const getCurrencyIcon = () => {
    const currencyValue = (currency as any)?.currency || "GBP";
    // Handle both currency codes (USD, EUR, GBP) and symbols ($, €, £)
    const currencyCode =
      currencyValue === "$" || currencyValue === "USD"
        ? "USD"
        : currencyValue === "€" || currencyValue === "EUR"
          ? "EUR"
          : currencyValue === "£" || currencyValue === "GBP"
            ? "GBP"
            : "GBP"; // Default to GBP

    switch (currencyCode) {
      case "USD":
        return <AttachMoneyIcon sx={{ fontSize: "20px" }} />;
      case "EUR":
        return <EuroIcon sx={{ fontSize: "20px" }} />;
      case "GBP":
      default:
        return <CurrencyPoundIcon sx={{ fontSize: "20px" }} />;
    }
  };

  // Dates and datetimes follow Europe/London in backoffice (COYN-1323).
  const formatDate = (dateString?: Date | string): string => {
    if (!dateString) return "N/A";
    return formatInBackofficeLondon(dateString, "DD MMM YYYY") || "N/A";
  };

  const formatDateTime = (dateString?: Date | string): string => {
    if (!dateString) return "N/A";
    return (
      formatInBackofficeLondon(dateString, "DD MMM YYYY, hh:mm A") || "N/A"
    );
  };

  // Format phone number
  const formatPhoneNumber = (
    phone?: string,
    countryPhoneCode?: string,
  ): string => {
    if (!phone) return "N/A";
    const fullPhone =
      countryPhoneCode && phone ? `+${countryPhoneCode}${phone}` : phone;
    // Format as +1 (283) 298-2283
    if (fullPhone.startsWith("+")) {
      const cleaned = fullPhone.replace(/\D/g, "");
      if (cleaned.length >= 10) {
        const country = cleaned.slice(0, -10);
        const area = cleaned.slice(-10, -7);
        const first = cleaned.slice(-7, -4);
        const last = cleaned.slice(-4);
        return `+${country} (${area}) ${first}-${last}`;
      }
    }
    return fullPhone;
  };

  // Format primary address
  // Note: If addressLine2 doesn't have a value, the comma should not show
  const formatPrimaryAddress = (): string => {
    if (!participant?.address) return "N/A";
    const addr = participant.address;
    const parts: string[] = [];
    if (addr.addressLine1) {
      parts.push(addr.addressLine1);
      if (addr.addressLine2) {
        parts.push(addr.addressLine2);
      }
    }
    if (addr.city) parts.push(addr.city);
    if (addr.state || addr.county) {
      parts.push(addr.state || addr.county || "");
    }
    if (addr.zipCode) parts.push(addr.zipCode);
    return parts.length > 0 ? parts.join(", ") : "N/A";
  };

  // Format secondary address
  // Note: If secondaryAddressLine2 doesn't have a value, the comma should not show
  const formatSecondaryAddress = (): string => {
    if (!participant?.address) return "N/A";
    const addr = participant.address;
    const parts: string[] = [];
    if (addr.secondaryAddressLine1) {
      parts.push(addr.secondaryAddressLine1);
      if (addr.secondaryAddressLine2) {
        parts.push(addr.secondaryAddressLine2);
      }
    }
    if (addr.secondaryCity) parts.push(addr.secondaryCity);
    if (addr.secondaryState || addr.secondaryCounty) {
      parts.push(addr.secondaryState || addr.secondaryCounty || "");
    }
    if (addr.secondaryZipCode) parts.push(addr.secondaryZipCode);
    return parts.length > 0 ? parts.join(", ") : "N/A";
  };

  const hasPrimaryAddressText = (): boolean => {
    const addr = participant?.address;
    return Boolean(addr?.addressLine1 || addr?.house);
  };

  const hasSecondaryAddressText = (): boolean => {
    return Boolean(participant?.address?.secondaryAddressLine1);
  };

  const primaryMissingCoordinates = (): boolean => {
    const addr = participant?.address;
    if (!hasPrimaryAddressText() || !addr) return false;
    return addr.latitude == null || addr.longitude == null;
  };

  const secondaryMissingCoordinates = (): boolean => {
    const addr = participant?.address;
    if (!hasSecondaryAddressText() || !addr) return false;
    return addr.secondaryLatitude == null || addr.secondaryLongitude == null;
  };

  const renderGeocodeFlag = () => (
    <Tooltip title={t("geocode.flag.tooltip")}>
      <Chip
        label={t("geocode.flag.label")}
        size="small"
        color="warning"
        data-testid="geocode-missing-coordinates-flag"
      />
    </Tooltip>
  );

  // Get mobile access status chip color
  const getMobileAccessStatusColor = (
    status?: string,
  ): { bgcolor: string; borderColor: string; color: string } => {
    if (status === "PENDING_VERIFICATION" || status === "PENDING")
      return {
        bgcolor: theme.palette.chipStatus.failed.fill, // Orange
        borderColor: theme.palette.chipStatus.failed.border,
        color: "#232B36", // Text/Dark 2
      };
    if (status === "ACTIVE")
      return {
        bgcolor: theme.palette.chipStatus.successful.fill, // Green
        borderColor: theme.palette.chipStatus.successful.border,
        color: "#232B36", // Text/Dark 2
      };
    // INACTIVE or default
    return {
      bgcolor: theme.palette.chipStatus.default.fill, // Gray
      borderColor: theme.palette.chipStatus.default.border,
      color: "#232B36", // Text/Dark 2
    };
  };

  // Get mobile access status label
  const getMobileAccessStatusLabel = (status?: string): string => {
    if (status === "PENDING_VERIFICATION") return "Pending";
    if (status === "ACTIVE") return "Active";
    if (status === "INACTIVE") return "Inactive";
    return "N/A";
  };

  // Handle copy email
  const handleCopyEmail = () => {
    if (participant?.email) {
      copyToClipboard(participant.email, () => {
        showSnackBar("Email address copied to clipboard", "success");
      });
    }
  };

  // Handle copy phone
  const handleCopyPhone = () => {
    if (participant?.phoneNumber && participant?.countryPhoneCode) {
      const fullPhone = `+${participant.countryPhoneCode}${participant.phoneNumber}`;
      copyToClipboard(fullPhone, () => {
        showSnackBar("Phone number copied to clipboard", "success");
      });
    }
  };

  // AC00-D: Loading skeletons
  if (isLoadingParticipant) {
    return (
      <>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mt={1}
          mb={1}
        >
          <Skeleton variant="rectangular" width={300} height={50} />
          <Stack direction="row" spacing={1}>
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="circular" width={40} height={40} />
          </Stack>
        </Box>

        <Box display="flex" gap={2}>
          <Box flexGrow={1}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Skeleton
                  variant="text"
                  width="60%"
                  height={32}
                  sx={{ mb: 2 }}
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Skeleton
                      variant="text"
                      width="80%"
                      height={24}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={24}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton
                      variant="text"
                      width="70%"
                      height={24}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="90%" height={24} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Skeleton variant="text" width="80%" height={24} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
          <Box width={230} flexShrink={0}>
            <Card sx={{ height: "100%", borderRadius: 2 }}>
              <CardContent>
                <Skeleton
                  variant="text"
                  width="60%"
                  height={32}
                  sx={{ mb: 2 }}
                />
                <Skeleton variant="text" width="80%" height={40} />
              </CardContent>
            </Card>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={1}
        mb={1}
      >
        <Typography
          color={theme.palette.textMain.dark2}
          variant="headlineLarge"
        >{`${participant?.firstName} ${participant?.lastName}`}</Typography>
        <Stack direction="row" spacing={1}>
          {/* COYN-1507: entry to the participant timeline (PART_TIMELINE_VIEW) */}
          <TimelineLinkButton
            participantId={participant?._id}
            variant="icon"
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              border: `1px solid ${theme.palette.primary.dark}`,
              height: "40px",
              width: "40px",
              color: theme.palette.primary.dark,
            }}
          />
          <Tooltip title="Audit log">
            <IconButton
              size="small"
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                border: `1px solid ${theme.palette.primary.dark}`,
                height: "40px",
                width: "40px",
                marginRight: 1,
              }}
              onClick={handleAuditLogs}
            >
              <HistoryIcon sx={{ color: theme.palette.primary.dark }} />
            </IconButton>
          </Tooltip>
          <ProfileMenu handleDelete={handleDelete} theme={theme} />
        </Stack>
      </Box>

      <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2}>
        {/* AC01: Personal Information Card */}
        <Box sx={{ flex: 1, height: "100%", minHeight: "350px" }}>
          <Card sx={{ borderRadius: 2, height: "100%", minHeight: "350px" }}>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Typography
                  variant="headlineSmall"
                  color={theme.palette.textMain.dark2}
                  sx={{ fontWeight: "bold" }}
                >
                  Personal information
                </Typography>
                {/* AC03: Edit button */}
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleEditParticipant}
                  sx={{
                    borderRadius: 1,
                    textTransform: "none",
                    px: 2,
                    minHeight: "2.5rem",
                    height: "2.5rem",
                    fontSize: "13px",
                    fontWeight: 500,
                    padding: "0.5rem 1rem",
                    alignSelf: { xs: "flex-start", sm: "center" },
                    flexShrink: 0,
                  }}
                >
                  EDIT
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    {/* Last login */}
                    <Box>
                      <Typography variant="bodyLarge" fontWeight={"bold"}>
                        Last login:{" "}
                      </Typography>
                      <Typography variant="bodyLarge">
                        {formatDateTime(participant?.mobileUser?.lastLoginDate)}
                      </Typography>
                    </Box>

                    {/* Client ID */}
                    <Box>
                      <Typography variant="bodyLarge" fontWeight={"bold"}>
                        Client ID:{" "}
                      </Typography>
                      <Typography variant="bodyLarge">
                        {participant?.cin || "N/A"}
                      </Typography>
                    </Box>

                    {/* Date of birth */}
                    <Box>
                      <Typography variant="bodyLarge" fontWeight={"bold"}>
                        Date of birth:{" "}
                      </Typography>
                      <Typography variant="bodyLarge">
                        {formatDate(participant?.dateOfBirth)}
                      </Typography>
                    </Box>

                    {/* Primary address */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="bodyLarge"
                        fontWeight={"bold"}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Primary address:{" "}
                      </Typography>
                      <Typography variant="bodyLarge">
                        {formatPrimaryAddress()}
                      </Typography>
                      {primaryMissingCoordinates() && renderGeocodeFlag()}
                    </Box>

                    {/* Secondary address */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="bodyLarge"
                        fontWeight={"bold"}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Secondary address:{" "}
                      </Typography>
                      <Typography variant="bodyLarge">
                        {formatSecondaryAddress()}
                      </Typography>
                      {secondaryMissingCoordinates() && renderGeocodeFlag()}
                    </Box>

                    {/* Mobile access status */}
                    <Box
                      sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
                    >
                      <Typography
                        variant="bodyLarge"
                        fontWeight={"bold"}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Mobile access status:{" "}
                      </Typography>
                      <Chip
                        label={getMobileAccessStatusLabel(
                          participant?.mobileUser?.status,
                        )}
                        sx={{
                          backgroundColor: getMobileAccessStatusColor(
                            participant?.mobileUser?.status,
                          ).bgcolor,
                          borderColor: getMobileAccessStatusColor(
                            participant?.mobileUser?.status,
                          ).borderColor,
                          color: getMobileAccessStatusColor(
                            participant?.mobileUser?.status,
                          ).color,
                          fontSize: "13px !important",
                          height: "24px",
                        }}
                        size="small"
                      />
                    </Box>

                    {/* Email and Phone side by side */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      {/* Email with copy icon */}
                      {participant?.email && (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            bgcolor: theme.palette.surface.light,
                            borderRadius: 1,
                            border: "1px solid #D2D2D2",
                            width: "fit-content",
                          }}
                        >
                          <MailOutlineIcon
                            sx={{ fontSize: "16px" }}
                            color="action"
                          />
                          <Typography
                            variant="labelSmall"
                            sx={{ fontSize: "13px" }}
                          >
                            {participant.email}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={handleCopyEmail}
                            sx={{
                              p: 0.25,
                              ml: 0.5,
                              minHeight: "16px !important",
                              minWidth: "16px !important",
                              height: "16px",
                              width: "16px",
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        </Box>
                      )}

                      {/* Phone with copy icon */}
                      {participant?.phoneNumber &&
                        participant?.countryPhoneCode && (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              px: 1,
                              py: 0.5,
                              bgcolor: theme.palette.surface.light,
                              borderRadius: 1,
                              border: "1px solid #D2D2D2",
                              width: "fit-content",
                            }}
                          >
                            <PhoneIcon
                              sx={{ fontSize: "16px" }}
                              color="action"
                            />
                            <Typography
                              variant="labelSmall"
                              sx={{ fontSize: "13px" }}
                            >
                              {formatPhoneNumber(
                                participant.phoneNumber,
                                participant.countryPhoneCode,
                              )}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={handleCopyPhone}
                              sx={{
                                p: 0.25,
                                ml: 0.5,
                                minHeight: "16px !important",
                                minWidth: "16px !important",
                                height: "16px",
                                width: "16px",
                              }}
                            >
                              <ContentCopyIcon sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </Box>
                        )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>

        {/* Incentives Overview Card */}
        <Box sx={{ flex: 1, height: "100%", minHeight: "350px" }}>
          <Card sx={{ borderRadius: 2, height: "100%", minHeight: "350px" }}>
            <CardContent>
              <Box
                sx={{ mb: 2 }}
                display="flex"
                flexDirection="column"
                gap={0.5}
              >
                <Typography
                  variant="headlineSmall"
                  color={theme.palette.textMain.dark2}
                  sx={{ fontWeight: "bold" }}
                >
                  Incentives overview
                </Typography>
                <Typography
                  variant="bodySmall"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Throughout all programs
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box>
                  <Typography variant="bodyLarge" fontWeight={"bold"}>
                    Incentives earned:{" "}
                  </Typography>
                  <Typography variant="bodyLarge">
                    {currency.currency}
                    {formatIncentiveAmount(participant?.totalIncentivesEarned)}
                  </Typography>
                </Box>
                {participant?.maxEarnings != null &&
                  participant?.currentYearIncentivesAmount != null && (
                    <Box>
                      <Typography variant="bodyLarge" fontWeight={"bold"}>
                        {new Date().getFullYear()} incentives:{" "}
                      </Typography>
                      <Typography variant="bodyLarge">
                        {currency.currency}
                        {formatIncentiveAmount(
                          participant?.currentYearIncentivesAmount,
                        )}{" "}
                        of {currency.currency}
                        {formatIncentiveAmount(participant?.maxEarnings)}
                      </Typography>
                    </Box>
                  )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Total Reward Balance Card */}
        <Box
          sx={{ width: 230, flexShrink: 0, height: "100%", minHeight: "350px" }}
        >
          <Card
            sx={{
              height: "100%",
              minHeight: "350px",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                "&:last-child": {
                  paddingBottom: 2,
                },
              }}
            >
              <Box>
                <Box display="flex" alignItems="center" mb={2} gap={1}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: "#F4F4F9",
                      border: "1px solid #C6C2DF",
                      color: "#6A6F81",
                    }}
                  >
                    {getCurrencyIcon()}
                  </Avatar>
                  <Typography
                    variant="headlineSmall"
                    color={theme.palette.textMain.dark2}
                    sx={{ fontWeight: "bold" }}
                  >
                    Total reward balance
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="start">
                  <Typography
                    variant="headlineLarge"
                    color={theme.palette.textMain.dark2}
                    sx={{ fontWeight: "400", fontSize: "3rem" }}
                  >
                    {currency.currency}
                    {formatBalanceAmount(participant?.totalBankBalance || 0)}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleTransactionHistory}
                  fullWidth
                  sx={{
                    borderRadius: 1,
                    textTransform: "uppercase",
                    fontSize: "16px",
                    fontWeight: 500,
                    padding: "0.5rem 1rem",
                  }}
                >
                  Transactions
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default ProfileCards;
